import { FormattedNumberInputField, TextareaInputField } from "@/components/shared";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { TFunction } from "i18next";
import { UseFormReturn } from "react-hook-form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import RenderFormRowComponent from "./RenderFormRowComponent";
import { stringToNumberOrUndefined } from "@/utils/form";

/**
 * Creates a zod schema for the estimate form group
 * All fields are requirements are based off quickoffer tender type, public and private tenders validation handled in main schema
 * @param t - The translation function
 * @returns The zod schema
 */
export const createEstimateSchema = (t: TFunction) => {
  return z.object({
    estimate: z.object({
      revenue: z.preprocess(
        stringToNumberOrUndefined,
        z.number({
          error: (issue) => issue.input === undefined ?
            t("schemas:createtender.estimate.revenuerequired")
            :
            t("schemas:createtender.estimate.revenuenumber"),
        })
          .int(t("schemas:createtender.estimate.revenueinteger"))
          .positive(t("schemas:createtender.estimate.revenuepositive"))
          .optional()
      ),
      hoursPerMonth: z.preprocess(
        stringToNumberOrUndefined,
        z.number({
          error: (issue) => issue.input === undefined ?
            t("schemas:createtender.estimate.hourspermonthrequired")
            :
            t("schemas:createtender.estimate.hourspermonthnumber"),
        })
          .int(t("schemas:createtender.estimate.hourspermonthinteger"))
          .positive(t("schemas:createtender.estimate.hourspermonthpositive"))
          .optional()
      ),
      margin: z.preprocess(
        stringToNumberOrUndefined,
        z.number({
          error: (issue) => issue.input === undefined ?
            t("schemas:createtender.estimate.marginrequired")
            :
            t("schemas:createtender.estimate.margininvalid"),
        })
          .min(0)
          .max(100)
          .optional()
      ),
      effort: z.preprocess(
        stringToNumberOrUndefined,
        z.number({
          error: (issue) => issue.input === undefined ?
            t("schemas:form.required")
            :
            t("schemas:form.invalid"),
        })
          .int()
          .min(1, t("schemas:createtender.estimate.effortmin"))
          .optional()
      ),
      description: z.string()
        .max(500, {
          message: t("schemas:createtender.estimate.descriptionmax"),
        }).optional(),
    }),
  });
};

/**
 * EstimateFormGroup component for editing the estimate form group
 * Utilises newtender locales as these values are based off the new tender form, if newtender changes, these will change with it.
 * @param form - The form object
 * @returns The rendered estimate form group component
 */
export function EstimateFormGroup({ form }: { form: UseFormReturn<any> }) {
  const { t } = useTranslation();

  return (
    <Card id="estimate">
      <CardHeader className="pt-4 pb-2 select-none">
        <CardTitle className="text-lg">{t("newtender.formgroups.estimate.title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        <div>
          {/* revenue */}
          <RenderFormRowComponent label={t("newtender.formgroups.estimate.inputs.revenue.label")}>
            <FormattedNumberInputField
              name="estimate.revenue"
              placeholder={t("newtender.formgroups.estimate.inputs.revenue.placeholder")}
              control={form.control}
              isInteger
              style="currency"
            />
            <p className="input-error-message mt-2">{form.formState.errors.estimate?.["revenue"]?.message}</p>
          </RenderFormRowComponent>

          {/* hoursPerMonth */}
          <RenderFormRowComponent label={t("newtender.formgroups.estimate.inputs.hourspermonth.label")}>
            <FormattedNumberInputField
              control={form.control}
              name="estimate.hoursPerMonth"
              placeholder={t("newtender.formgroups.estimate.inputs.hourspermonth.placeholder")}
              additionalSuffix={t("newtender.formgroups.estimate.inputs.hourspermonth.suffix")}
              isInteger
              spaceBetweenSuffix={true}
            />
            <p className="input-error-message mt-2">{form.formState.errors.estimate?.["hoursPerMonth"]?.message}</p>
          </RenderFormRowComponent>

          {/* Margin */}
          <RenderFormRowComponent label={t("newtender.formgroups.estimate.inputs.margin.label")}>
            <FormattedNumberInputField
              control={form.control}
              name="estimate.margin"
              placeholder={t("newtender.formgroups.estimate.inputs.margin.placeholder")}
              additionalSuffix="%"
            />
            <p className="input-error-message mt-2">{form.formState.errors.estimate?.["margin"]?.message}</p>
          </RenderFormRowComponent>

          {/* Effort */}
          <RenderFormRowComponent label={t("newtender.formgroups.estimate.inputs.effort.label")}>
            <FormattedNumberInputField
              control={form.control}
              name="estimate.effort"
              placeholder={t("newtender.formgroups.estimate.inputs.effort.placeholder")}
              additionalSuffix={t("newtender.formgroups.estimate.inputs.effort.suffix")}
              isInteger
              spaceBetweenSuffix={true}
            />
            <p className="input-error-message mt-2">{form.formState.errors.estimate?.["effort"]?.message}</p>
          </RenderFormRowComponent>

          {/* Description */}
          <RenderFormRowComponent label={t("newtender.formgroups.estimate.inputs.description.label")}>
            <TextareaInputField
              form={form}
              name="estimate.description"
              placeholder={t("newtender.formgroups.estimate.inputs.description.placeholder")}
              isFormItem={false}
              className="w-full"
            />
          </RenderFormRowComponent>
        </div>
      </CardContent>
    </Card>
  );
}
